/**
 * condition-serializer.ts — pure, load-bearing serialization layer.
 *
 * TWO formats keyed by catalog eval_level:
 *   probe_result (http/https/tcp/dns/tls):
 *     Mongo-style { field: { $op: value } }
 *     Multi-row: { $and: [...] } or { $or: [...] }
 *     Single-row: { field: { $op: value } } (no wrapping)
 *
 *   data_item (elastic/waap/delivery):
 *     Array [{ key, operator, value }, ...]  — implicit AND, no mode toggle.
 *
 * Value coercion is driven by CatalogField.kind:
 *   number → JS number (parseFloat)
 *   bool   → JS boolean
 *   string → JS string
 *   $in/$nin operators → array of the above type
 *
 * Edit-load parser (inverse) is tolerant:
 *   - Single Mongo clause { field: { $op: val } }
 *   - Multi Mongo { $and: [...] } or { $or: [...] }
 *   - Array form [{ key, op, val }]
 *   - Unparseable → { rows: [], rawFallback: string }
 */

import type { EvalLevel, FieldKind } from '@/api/healthcheck';

// ---- Row (internal UI representation) -----------------------------------

export interface ConditionRow {
  /** Catalog field name, e.g. "status_code", "total_threats" */
  field: string;
  /** Operator string, e.g. "$gte", "$eq", "$in" */
  operator: string;
  /** Typed value; array for $in/$nin */
  value: unknown;
}

// ---- Serialise (rows → wire JSON) ----------------------------------------

/** Coerce a raw string input into the typed value for the given kind/operator. */
export function coerceValue(raw: unknown, kind: FieldKind, operator: string): unknown {
  const isMulti = operator === '$in' || operator === '$nin';

  if (isMulti) {
    // raw may already be an array (from tag-input) or a comma-string
    const arr: unknown[] = Array.isArray(raw)
      ? (raw as unknown[])
      : String(raw ?? '')
          .split(',')
          .map((s) => s.trim())
          .filter(Boolean);
    return arr.map((item) => coerceSingle(item, kind));
  }

  return coerceSingle(raw, kind);
}

/**
 * Coerce a scalar to its typed form. Returns the original value unchanged when
 * it is already the correct type — this avoids re-stringifying numbers that
 * arrived as JS numbers from the parser (which preserves JSON types verbatim).
 * For number kind: returns NaN when the string cannot be parsed, so callers
 * (validateRows) can detect and reject the invalid input rather than silently
 * sending 0 to the BE.
 */
function coerceSingle(raw: unknown, kind: FieldKind): unknown {
  if (kind === 'number') {
    if (typeof raw === 'number') return raw;         // already a JS number — pass through
    const n = parseFloat(String(raw ?? ''));
    return n;                                        // may be NaN — validateRows will catch it
  }
  if (kind === 'bool') {
    if (typeof raw === 'boolean') return raw;
    return String(raw).toLowerCase() === 'true';
  }
  return String(raw ?? '');
}

/**
 * Serialise rows to probe_result (Mongo) format.
 * mode 'and' | 'or' only applies when rows.length > 1.
 */
export function toProbeResult(rows: ConditionRow[], mode: 'and' | 'or' = 'and'): unknown {
  if (rows.length === 0) return {};
  const clauses = rows.map((r) => ({ [r.field]: { [r.operator]: r.value } }));
  if (clauses.length === 1) return clauses[0];
  return { [`$${mode}`]: clauses };
}

/** Serialise rows to data_item (array) format. */
export function toDataItem(rows: ConditionRow[]): unknown {
  return rows.map((r) => ({ key: r.field, operator: r.operator, value: r.value }));
}

/**
 * Top-level serialise: chooses format from catalog eval_level.
 * Returns the object/array ready to be sent as `conditions` in the request body.
 */
export function serializeConditions(
  rows: ConditionRow[],
  evalLevel: EvalLevel,
  mode: 'and' | 'or' = 'and',
): unknown {
  if (evalLevel === 'probe_result') return toProbeResult(rows, mode);
  return toDataItem(rows);
}

// ---- Parse (wire JSON → rows) --------------------------------------------

export interface ParseResult {
  rows: ConditionRow[];
  mode: 'and' | 'or';
  /** Non-null when conditions could not be parsed into rows (raw escape hatch). */
  rawFallback: string | null;
}

/**
 * Parse stored conditions back to UI rows.
 * Tolerant: handles single-clause, $and/$or, array form, and raw-JSON fallback.
 */
export function parseConditions(raw: unknown): ParseResult {
  if (raw === null || raw === undefined) {
    return { rows: [], mode: 'and', rawFallback: null };
  }

  // ---- Array form (data_item) ------------------------------------------
  if (Array.isArray(raw)) {
    try {
      const rows: ConditionRow[] = (raw as Record<string, unknown>[]).map((item) => ({
        field: String(item.key ?? ''),
        operator: String(item.operator ?? ''),
        value: item.value,
      }));
      return { rows, mode: 'and', rawFallback: null };
    } catch {
      return { rows: [], mode: 'and', rawFallback: JSON.stringify(raw, null, 2) };
    }
  }

  // ---- Mongo object form (probe_result) --------------------------------
  if (typeof raw === 'object' && raw !== null) {
    const obj = raw as Record<string, unknown>;
    const keys = Object.keys(obj);

    // $and / $or wrapper
    if (keys.length === 1 && (keys[0] === '$and' || keys[0] === '$or')) {
      const mode: 'and' | 'or' = keys[0] === '$and' ? 'and' : 'or';
      const clauses = obj[keys[0]];
      if (Array.isArray(clauses)) {
        try {
          const rows = clauses.map((clause) => parseMongoClause(clause));
          if (rows.every(Boolean)) {
            return { rows: rows as ConditionRow[], mode, rawFallback: null };
          }
        } catch { /* fall through to rawFallback */ }
      }
    }

    // Single-clause { field: { $op: value } }
    try {
      const row = parseMongoClause(obj);
      if (row) return { rows: [row], mode: 'and', rawFallback: null };
    } catch { /* fall through */ }

    // Unparseable object
    return { rows: [], mode: 'and', rawFallback: JSON.stringify(raw, null, 2) };
  }

  // Scalar or unknown — raw fallback
  return { rows: [], mode: 'and', rawFallback: JSON.stringify(raw, null, 2) };
}

/**
 * Parse a single Mongo field clause `{ field: { $op: value } }` → ConditionRow.
 * Returns null if the shape doesn't match (e.g. op key doesn't start with $).
 */
function parseMongoClause(obj: unknown): ConditionRow | null {
  if (!obj || typeof obj !== 'object') return null;
  const record = obj as Record<string, unknown>;
  const keys = Object.keys(record);
  // Must be exactly one non-$ key (the field name)
  if (keys.length !== 1) return null;
  const fieldKey = keys[0];
  if (fieldKey.startsWith('$')) return null;
  const opMap = record[fieldKey];
  if (!opMap || typeof opMap !== 'object' || Array.isArray(opMap)) return null;
  const opObj = opMap as Record<string, unknown>;
  const opKeys = Object.keys(opObj);
  if (opKeys.length !== 1) return null;
  const operator = opKeys[0];
  // Operator must start with $ to be a valid Mongo operator
  if (!operator.startsWith('$')) return null;
  return { field: fieldKey, operator, value: opObj[operator] };
}

// ---- Re-coerce all rows by catalog kind (submit-time coercion) -----------

import type { CatalogField } from '@/api/healthcheck';

/**
 * Re-coerce every row's value by the field's kind from the catalog.
 * Call this right before serializeConditions in BOTH create and edit paths.
 * Guarantees: number kind → JS number, bool → boolean, $in/$nin → typed array.
 * Rows with unknown fields (not in catalog) are passed through unchanged.
 */
export function recoerceRows(rows: ConditionRow[], fields: CatalogField[]): ConditionRow[] {
  const fieldMap = new Map(fields.map((f) => [f.name, f]));
  return rows.map((row) => {
    const field = fieldMap.get(row.field);
    if (!field) return row;
    return { ...row, value: coerceValue(row.value, field.kind, row.operator) };
  });
}

// ---- Validation ----------------------------------------------------------

export interface RowValidationError {
  index: number;
  message: string;
}

/**
 * Validate rows before serialisation. Returns error list (empty = valid).
 * Must be called AFTER recoerceRows so number types are already coerced.
 */
export function validateRows(rows: ConditionRow[]): RowValidationError[] {
  const errors: RowValidationError[] = [];
  if (rows.length === 0) {
    errors.push({ index: -1, message: 'At least one condition is required.' });
    return errors;
  }
  rows.forEach((row, i) => {
    if (!row.field) errors.push({ index: i, message: 'Field is required.' });
    if (!row.operator) errors.push({ index: i, message: 'Operator is required.' });
    const isMulti = row.operator === '$in' || row.operator === '$nin';
    if (isMulti) {
      if (!Array.isArray(row.value) || (row.value as unknown[]).length === 0) {
        errors.push({ index: i, message: 'At least one value required for $in/$nin.' });
      } else {
        // Validate each element in the array is a valid number if applicable
        (row.value as unknown[]).forEach((v) => {
          if (typeof v === 'number' && !Number.isFinite(v)) {
            errors.push({ index: i, message: 'All values must be valid numbers.' });
          }
        });
      }
    } else if (row.value === '' || row.value === undefined || row.value === null) {
      errors.push({ index: i, message: 'Value is required.' });
    } else if (typeof row.value === 'number' && !Number.isFinite(row.value)) {
      // H2: reject NaN/Infinity — coerceSingle now returns NaN for invalid numeric input
      errors.push({ index: i, message: 'Enter a valid number.' });
    }
  });
  return errors;
}
